import { useRef, useState, type CSSProperties } from "react";
import { IconButton } from "@/components/common/IconButton";
import { updateEnvironment, type Environment } from "@/lib/api";
import { useToastStore } from "@/stores/useToastStore";

interface EditableEnvironmentNameProps {
	environment: Environment;
	onRenamed: (renamed: Environment) => void;
}

// 表示と入力で文字の位置が動かないよう、同じ余白と文字設定を共有する。左の余白ぶん外へずらし、
// 文字の左端を周りの要素と揃える。枠は文字の幅に合わせ、親の幅を超えたら省略する。
const NAME_BOX_STYLE: CSSProperties = {
	boxSizing: "border-box",
	maxWidth: "calc(100% + 6px)",
	margin: "0 0 0 -6px",
	padding: "2px 6px",
	border: 0,
	borderRadius: "var(--radius-2)",
	fontFamily: "inherit",
	fontSize: "var(--text-heading)",
	fontWeight: 600,
	lineHeight: "var(--leading-normal)",
	color: "var(--fg-1)",
};

/**
 * エージェント名の見出し。マウスが同じ行に来たら枠と右の鉛筆アイコンを出し、クリックで入力欄に切り替える。フォーカスが外れたら
 * (Enter でも) 保存し、Escape で取り消す。空のときと変わっていないときは保存せずに戻す。
 */
export function EditableEnvironmentName({ environment, onRenamed }: EditableEnvironmentNameProps) {
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState(environment.name);
	const [hovered, setHovered] = useState(false);
	// 保存中は送った名前を先に見せ、失敗したら元の名前へ戻す。
	const [pendingName, setPendingName] = useState<string | null>(null);
	// Escape で閉じたときに、入力欄が外れる際の blur で保存しないための印。
	const cancelledRef = useRef(false);

	const startEditing = () => {
		cancelledRef.current = false;
		setDraft(environment.name);
		setEditing(true);
	};

	const commit = async () => {
		setEditing(false);
		if (cancelledRef.current) return;
		const name = draft.trim();
		if (name === "" || name === environment.name) return;
		setPendingName(name);
		try {
			onRenamed(await updateEnvironment(environment.id, name));
		} catch (error) {
			useToastStore.getState().show({
				type: "error",
				message: error instanceof Error ? error.message : "エージェント名の変更に失敗しました。",
			});
			throw error;
		} finally {
			setPendingName(null);
		}
	};

	if (editing) {
		return (
			<input
				// クリックした直後にそのまま打ち込めるようにする。
				autoFocus
				value={draft}
				onChange={(e) => setDraft(e.target.value)}
				onFocus={(e) => e.currentTarget.select()}
				onBlur={() => void commit()}
				onKeyDown={(e) => {
					if (e.key === "Enter") e.currentTarget.blur();
					if (e.key === "Escape") {
						// 取り消しに使った Escape で、外側のポップオーバーまで閉じないようにする。
						e.preventDefault();
						cancelledRef.current = true;
						setEditing(false);
					}
				}}
				data-testid="environment-name-input"
				style={{
					...NAME_BOX_STYLE,
					// 入力中の文字数に合わせて伸び縮みさせ、表示中の枠と同じ幅から始める。
					fieldSizing: "content",
					minWidth: "2em",
					alignSelf: "flex-start",
					outline: "none",
					background: "var(--surface-2)",
					boxShadow: "var(--shadow-focus)",
				}}
			/>
		);
	}

	const name = pendingName ?? environment.name;
	return (
		<h4
			onPointerEnter={() => setHovered(true)}
			onPointerLeave={() => setHovered(false)}
			style={{ display: "flex", alignItems: "center", gap: 2, margin: 0, minWidth: 0 }}
		>
			<button
				type="button"
				onClick={startEditing}
				data-testid="environment-name-heading"
				style={{
					...NAME_BOX_STYLE,
					minWidth: 0,
					overflow: "hidden",
					textOverflow: "ellipsis",
					whiteSpace: "nowrap",
					textAlign: "left",
					background: "transparent",
					boxShadow: hovered ? "var(--shadow-ring)" : "none",
					cursor: "text",
				}}
			>
				{name}
			</button>
			<IconButton
				icon="pencil"
				iconSize={14}
				size={20}
				tabIndex={-1}
				onClick={startEditing}
				data-testid="environment-name-edit-icon"
				// 出し入れで名前の幅が変わらないよう、隠す間も場所は取っておく。
				style={{ flexShrink: 0, opacity: hovered ? 1 : 0 }}
			/>
		</h4>
	);
}
